import React, { useContext } from 'react'
import useSiteData from '../useSiteData'
import { Title, Link, Meta } from 'react-head'
import { getCurrentLocale, locales } from '../api/index'
import appleTouchIcon from '../resources/favicon/apple-touch-icon.png'
import favIcon from '../resources/favicon/favicon.ico'
import favIcon32x32 from '../resources/favicon/favicon-32x32.png'
import favIcon16x16 from '../resources/favicon/favicon-16x16.png'
import safariPinnedTab from '../resources/favicon/safari-pinned-tab.svg'
import { getLocaleInfo } from '../util/contentModelToData'

const getFBVerificationCode = localeCode => {
  switch (localeCode) {
    case 'en-GB':
    case 'en-US':
      return 'aguojmsmau71wu4ipqyrxhvtt8gly2'
    case 'sv-SE':
      return 'hmox5y2rllq57kw94e2jnjy0ldy4uw'
    case 'nb-NO':
      return 'am6ki3f6erg8wp9plgoot6me51sldc'
    case 'da-DK':
      return 'meaoviiy271okuqejodkp4vafhujhg'
    case 'de-DE':
      return 'fff4633u05u7myn22zdxc6h5db0gvv'
    case 'de-CH':
      return 'qmoq44waxsn69l211gnzprlwio4kz0'
    case 'de-AT':
      return 'slee8pukcb5tiesd8eh2optvqhwr7p'
    case 'fr-FR':
      return '4bslqebbtmoi7msopc0pbeiqvbxrro'
  }
}

export default ({ title, description, image, canonical, slugs, pagePath, noIndexFollow=false, robots }) => {
  const { title: defaultTitle, description: defaultDescription, image: defaultImage, enableOptimizeAntiFlicker, optimizeContainerId } = useSiteData().pageSettings
  const currentLocale = getCurrentLocale()

  const hrefLangLinks = slugs ? locales.map(locale => {
    const path = slugs[locale]
    const domain = getLocaleInfo(locale).link
    // do not append extra slash to home page url, causing www.lapoint.se// etc
    const href = domain + (path !== '/' ? path + '/' : '')
    // add x-default only if the content exists in english
    if( path && locale == 'en-US') {
      return (
        <React.Fragment key={ locale }>
          <Link rel="alternate" hrefLang={ locale } href={ href } />
          <Link rel="alternate" hrefLang="x-default" href={ href } /> 
        </React.Fragment>
      )
    }
    return path ? <Link key={ locale } rel="alternate" hrefLang={ locale } href={ href } /> : null
  }) : null

  let metaDescription = description || defaultDescription

  if( metaDescription === undefined ) {
    metaDescription = ''
  }

  // Under pageSettings we have enableOptimizeAntiFlicker and optimizeContainerId
  // The original thought was to use that to determine if we should add Optimize Anti Flicker Snippet.
  // However when Optimize.js is loaded early (and not as previously deployed from Google Tag Manager) there is no need
  // to add the Anti Flicker Snippet. So now we use it to determine if we should add Optimize.js or not.
  //
  // Since react-head does not support <Script> tags the workaround is to add <Meta name="enableOptimize"> and in gatsby-ssr.js onPreRenderHTML
  // we add Optimize.js if the meta tag is present.
  const enableOptimize = enableOptimizeAntiFlicker?.indexOf( currentLocale ) > -1 ? true : false
  const optimizeContainerID = optimizeContainerId || "OPT-TVJ8B29"

  // so we can set og:url on SSR since ahrefs.com complains about it is missing
  const pageUrl = getLocaleInfo(currentLocale).link + ( pagePath !== '/' ? pagePath + '/' : '' )
  
  // ahrefs.com says images starting with //path.to.image are invalid. So lets add a https protcol
  // nice one liner found here: https://stackoverflow.com/a/61934195/6833062 to make sure it starts with https://
  const withHttps = (url) => url.replace(/^(?:(.*:)?\/\/)?(.*)/i, (match, schemma, nonSchemmaUrl) => schemma ? match : `https://${nonSchemmaUrl}`)
  let shareImage = false
  if( image || defaultImage ) {
    shareImage = withHttps( (image || defaultImage).file.url )
  } 

  // noIndexFollow will be set to true for blog archive pages that should have robots = noindex, follow i.e for all paginated blog archive pages
  // All test pages, i.e pages where the slug starts with test-page/ should have noindex, nofollow we do this by checking the pagePath
  // Lapoint preview content, i.e if the slug starts with lcp/ should have noindex, nofollow
  // Pages and LocAct pages have the field metaDataRobots that is passed to the variable robots. If present and the above two is not true then use it

  let robotsContent = null
  if( noIndexFollow ) {
    // it's a blog archive page that should not be indexed, but followed so that it does index individual blogposts
    robotsContent = "noindex, follow"  
  } else if( pagePath.indexOf( 'test-page/' ) !== -1 ) {
    // test pages 
    robotsContent = "noindex, nofollow"
  } else if( robots !== undefined && Array.isArray( robots ) ) {
    // use value from CMS
    robotsContent = robots.toString().toLowerCase()
  }

  return (
    <>
      { enableOptimize &&
        <Meta name="enableOptimize" content={optimizeContainerID} />
      }
      { robotsContent &&
        <Meta name="robots" content={ robotsContent } />
      }
      <Meta charSet="UTF-8"/>
      <Meta name="viewport" content="width=device-width, initial-scale=1"/>
      <Title>{ title || defaultTitle }</Title>
      <Meta name="description" content={ metaDescription }/>
      <Meta property="og:site_name" content="Lapoint"/>
      <Meta property="og:type" content="website"/>
      <Meta property="og:url" content={ pageUrl }/> 
      <Meta property="og:locale" content={ currentLocale }/>
      <Meta property="og:title" content={ title || defaultTitle }/>
      <Meta property="og:description" content={ metaDescription }/>
      { shareImage &&
        <Meta property="og:image" content={ shareImage }/>
      }
      <Meta name="twitter:card" content="summary_large_image"/>
      <Meta name="twitter:title" content={ title || defaultTitle }/>
      <Meta name="twitter:description" content={ metaDescription }/>
      { shareImage &&
        <Meta name="twitter:image" content={ shareImage }/>
      }
      { (typeof window !== 'undefined' && canonical) ?
        <Link rel="canonical" href={ window.location.origin + '/' + canonical }/> : '' 
      }
      <Link rel="apple-touch-icon" sizes="180x180" href={ appleTouchIcon }/>
      <Link rel="icon" sizes="48x48" href={ favIcon }/>
      <Link rel="icon" type="image/png" sizes="32x32" href={ favIcon32x32 }/>
      <Link rel="icon" type="image/png" sizes="16x16" href={ favIcon16x16 }/>
      <Link rel="mask-icon" href={ safariPinnedTab } color="#fb322b"/>
      { hrefLangLinks }
      <Meta name="msapplication-TileColor" content="#ffffff"/>
      <Meta name="theme-color" content="#ffffff"/>
      { pagePath == '/' &&
        <Meta name="facebook-domain-verification" content={ getFBVerificationCode(currentLocale) } />
      }
    </>
  )
};
